import JSZip from "jszip";
import { z } from "zod";

/** Public template surface: cover/guts template files and product metadata for any published product. */

export type TemplateArtifact = "cover" | "pages";

export const TEMPLATE_FORMATS = ["pdf", "png", "idml"] as const;
export type TemplateFormat = (typeof TEMPLATE_FORMATS)[number];

export type PageSide = "left" | "right";

export type DownloadScope = "cover-and-pages" | "cover" | "pages";

const metadataSchema = z.object({
  specification: z.object({
    pages_spec: z.object({
      min_page_count: z.number().int().positive(),
    }),
  }),
});

/** The subset of the public template-metadata envelope the example consumes. */
export type TemplateMetadata = z.infer<typeof metadataSchema>;

export function artifactsForScope(scope: DownloadScope): TemplateArtifact[] {
  switch (scope) {
    case "cover":
      return ["cover"];
    case "pages":
      return ["pages"];
    case "cover-and-pages":
      return ["cover", "pages"];
  }
}

// PNG page templates render one image per spread side, so the pages artifact expands into two
// per-side files for PNG only; every other (artifact, format) combination is a single file.
export function pageSidesForFormat(artifact: TemplateArtifact, format: TemplateFormat): PageSide[] | [undefined] {
  if (artifact === "pages" && format === "png") return ["left", "right"];
  return [undefined];
}

export function normalizeBaseUrl(base: string | undefined | null): string {
  return (base ?? "").replace(/\/+$/, "");
}

/** The configured template base URL, or undefined when the env var is unset. */
export function templateApiBaseUrl(): string | undefined {
  const normalized = normalizeBaseUrl(import.meta.env.VITE_PUBLIC_TEMPLATE_ENDPOINT_URL);
  return normalized || undefined;
}

/** The footer label for a product: its name, falling back to its description when name is blank. */
export function resolveProductLabel(name?: string | null, description?: string | null): string {
  return name?.trim() || description?.trim() || "";
}
export function buildTemplateUrl(
  base: string,
  manufacturingSku: string,
  artifact: TemplateArtifact,
  params: {
    pageCount: number;
    productDescription: string;
    format?: TemplateFormat;
    units?: "in" | "mm";
    barcode?: boolean;
    side?: PageSide;
  }
): string {
  const format = params.format ?? "pdf";
  const url = new URL(`${base}/published/${encodeURIComponent(manufacturingSku)}/${artifact}/${format}`);
  if (params.units) {
    url.searchParams.set("units", params.units);
  }
  url.searchParams.set("pageCount", String(params.pageCount));
  if (params.productDescription) {
    url.searchParams.set("productDescription", params.productDescription);
  }
  if (params.barcode && artifact === "cover") {
    url.searchParams.set("barcode", "true");
  }
  if (params.side) {
    url.searchParams.set("side", params.side);
  }
  return url.toString();
}

/** Composes the caller-supplied footer description: the product label plus its page count. */
export function composeProductDescription(label: string, pageCount: number): string {
  const pages = `${pageCount} ${pageCount === 1 ? "page" : "pages"}`;
  const trimmed = label.trim();
  return trimmed ? `${trimmed} | ${pages}` : pages;
}

export function minPageCountFromMetadata(metadata: TemplateMetadata): number {
  return metadata.specification.pages_spec.min_page_count;
}

export interface OrderFiles {
  coverUrl: string;
  gutsUrl: string;
  productDescription: string;
}

/** Derives the cover and guts URLs and footer description for a product from its metadata. */
export function deriveOrderFiles(input: {
  base: string;
  manufacturingSku: string;
  label: string;
  metadata: TemplateMetadata;
}): OrderFiles {
  const pageCount = minPageCountFromMetadata(input.metadata);
  const productDescription = composeProductDescription(input.label, pageCount);
  return {
    productDescription,
    coverUrl: buildTemplateUrl(input.base, input.manufacturingSku, "cover", { pageCount, productDescription }),
    gutsUrl: buildTemplateUrl(input.base, input.manufacturingSku, "pages", { pageCount, productDescription }),
  };
}

/** Fetches and validates the by-SKU template metadata from the unauthenticated public surface. */
export async function fetchTemplateMetadata(base: string, manufacturingSku: string): Promise<TemplateMetadata> {
  const response = await fetch(`${base}/published/${encodeURIComponent(manufacturingSku)}/metadata`);
  if (!response.ok) {
    throw new Error(`Template metadata request failed (${response.status})`);
  }
  return metadataSchema.parse(await response.json());
}

export type RequestTemplateDownloadsParams = {
  base: string;
  manufacturingSku: string;
  format: TemplateFormat;
  scope: DownloadScope;
  pageCount: number;
  productDescription: (artifact: TemplateArtifact) => string;
  triggerDownload: (blob: Blob, filename: string) => void;
};

/** Turns a product description into a safe filename stem. Preserves `.` so decimal dimensions (e.g. `7.75x7.75`) survive. */
function sanitizeFilename(name: string): string {
  return name.replace(/[^\w\s.-]/g, "").replace(/\s+/g, "-");
}

// Fetches every file implied by (scope, format) as a blob. Zero-side artifacts contribute one file
// each; the PNG pages artifact contributes two (left/right). When more than one file is requested
// they are bundled into a single zip named after the cover description (or the first successful
// artifact if there is no cover); the zip is only produced when the full set succeeds. Single-file
// requests trigger one direct download. Any non-OK verdict or thrown error counts as a failed file.
export async function requestTemplateDownloads({
  base,
  manufacturingSku,
  format,
  scope,
  pageCount,
  productDescription,
  triggerDownload,
}: RequestTemplateDownloadsParams): Promise<{ requested: number; failed: number }> {
  const artifacts = artifactsForScope(scope);
  const fetched: { artifact: TemplateArtifact; side?: PageSide; description: string; blob: Blob }[] = [];
  let requested = 0;
  let failed = 0;
  for (const artifact of artifacts) {
    const description = productDescription(artifact);
    for (const side of pageSidesForFormat(artifact, format)) {
      requested += 1;
      try {
        const url = buildTemplateUrl(base, manufacturingSku, artifact, {
          pageCount,
          productDescription: description,
          format,
          units: "in",
          barcode: true,
          side,
        });
        const response = await fetch(url);
        if (!response.ok) {
          failed += 1;
          continue;
        }
        const blob = await response.blob();
        fetched.push({ artifact, side, description, blob });
      } catch {
        failed += 1;
      }
    }
  }

  const filenameFor = (entry: (typeof fetched)[number]) =>
    `${sanitizeFilename(entry.description)}-${entry.artifact}${entry.side ? `-${entry.side}` : ""}.${format}`;

  const archiveFilenameFor = (entries: typeof fetched) => {
    const coverEntry = entries.find((entry) => entry.artifact === "cover");
    const pagesEntry = entries.find((entry) => entry.artifact === "pages");
    // Label the archive by which artifact types it contains; the description supplies the product.
    const artifactLabel = coverEntry && pagesEntry ? "cover-and-pages" : coverEntry ? "cover" : "pages";
    const description = sanitizeFilename(coverEntry?.description || pagesEntry?.description || "");
    const stem = description ? `${description}-${artifactLabel}` : `${artifactLabel}-${pageCount}-pages`;
    return `${stem}.zip`;
  };

  if (requested > 1) {
    // Bundle mode: only produce a zip when the full set arrived; a partial zip would be misleading.
    if (fetched.length === requested) {
      const zip = new JSZip();
      for (const entry of fetched) {
        zip.file(filenameFor(entry), await entry.blob.arrayBuffer());
      }
      const zipBlob = await zip.generateAsync({ type: "blob" });
      triggerDownload(zipBlob, archiveFilenameFor(fetched));
    }
  } else if (fetched.length === 1) {
    triggerDownload(fetched[0].blob, filenameFor(fetched[0]));
  }

  return { requested, failed };
}
